import React, { useState, useEffect } from 'react';
import {
    Leaf,
    Droplets,
    Thermometer,
    Wind,
    Sparkles,
    ArrowUpRight,
    MoreHorizontal,
    TrendingUp,
    ChevronDown,
    Clock
} from 'lucide-react';
import { weatherService } from '../../services/WeatherService';
import { marketService } from '../../services/MarketService';
import { sensorService } from '../../services/SensorService';
import { agroLogic } from '../../services/AgroLogic';
import { fieldBoundaries } from '../../data/fieldBoundaries';

export default function ModernDashboard({ initialFieldId }) {
    const [weather, setWeather] = useState(null);
    const [marketData, setMarketData] = useState([]);
    const [sensorData, setSensorData] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Initial lookup for field info if provided
    const initialField = initialFieldId
        ? fieldBoundaries.features.find(f => f.properties.id === initialFieldId)
        : fieldBoundaries.features[0];

    // Interactivity State
    const [selectedField, setSelectedField] = useState(initialField?.properties || fieldBoundaries.features[0].properties);
    const [timeRange] = useState("Live");

    useEffect(() => {
        async function fetchData() {
            try {
                const [weatherData, prices] = await Promise.all([
                    weatherService.getCurrentWeather(),
                    marketService.getMarketPrices()
                ]);
                setWeather(weatherData);
                setMarketData(prices);
            } catch (err) {
                console.error("Failed to load dashboard data", err);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    useEffect(() => {
        // Subscribe to real-time sensor updates for the SELECTED field
        const unsubscribe = sensorService.subscribe((data) => {
            setSensorData(data);

            if (data) {
                const activeAlerts = agroLogic.analyze(data, weather, selectedField.crop);
                setAlerts(activeAlerts);
            }
        }, selectedField.id);

        return () => unsubscribe();
    }, [selectedField.id, selectedField.crop, weather]);

    // Calculate health score based on sensor data deviation from targets
    const healthScore = sensorData ? Math.max(0, Math.min(100, 100 - Math.abs(sensorData.moisture - 62) - Math.abs(sensorData.nitrogen - 42))) : 0;

    const handleRefresh = () => {
        sensorService.refresh(selectedField.id);
    };

    return (
        <div className="p-4 md:p-6 space-y-6 max-w-[1600px] mx-auto pb-24 font-sans text-text-primary">
            {/* Rugged Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-surface-card border border-border-strong p-4 rounded-xl shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight uppercase text-text-primary">Agrotrack Field Ops</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <span className={`w-2 h-2 rounded-full ${loading ? 'bg-yellow-500' : 'bg-primary'} animate-pulse`}></span>
                        <p className="text-xs font-mono text-text-secondary uppercase">
                            System Online • {timeRange} • Last Update: {sensorData?.lastUpdated || 'Syncing...'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleRefresh}
                        className="p-2 hover:bg-secondary rounded-lg border border-border-subtle hover:border-primary/50 text-text-muted hover:text-primary transition-all"
                        title="Force Data Sync"
                    >
                        <Clock className="w-4 h-4" />
                    </button>

                    {/* Sector Selector */}
                    <div className="relative group">
                        <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border-subtle hover:bg-secondary hover:border-primary/50 transition-all">
                            <div className="text-right">
                                <div className="text-[10px] text-text-secondary font-mono uppercase">Sector</div>
                                <div className="text-sm font-semibold leading-none truncate max-w-[150px]">{selectedField.name}</div>
                            </div>
                            <ChevronDown className="w-4 h-4 text-text-muted group-hover:text-primary transition-colors" />
                        </button>
                        {/* Dropdown Content */}
                        <div className="absolute right-0 top-full mt-2 w-56 max-h-64 overflow-y-auto bg-surface-card border border-border-strong rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                            {fieldBoundaries?.features?.slice(0, 12).map(feature => (
                                <div
                                    key={feature.properties.id}
                                    onClick={() => setSelectedField(feature.properties)}
                                    className={`px-4 py-2 hover:bg-secondary cursor-pointer text-sm first:rounded-t-lg last:rounded-b-lg border-b border-border/50 last:border-0 ${selectedField.id === feature.properties.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                                >
                                    <div className="font-semibold">{feature.properties.name}</div>
                                    <div className="text-xs text-muted-foreground">{feature.properties.crop} • {feature.properties.area} ac</div>
                                </div>
                            ))}
                            {(!fieldBoundaries?.features || fieldBoundaries.features.length === 0) && (
                                <div className="px-4 py-2 text-sm text-muted-foreground">No fields active</div>
                            )}
                        </div>
                    </div>

                    <div className="h-8 w-px bg-border-subtle mx-2"></div>

                    <div className="text-right">
                        <div className="text-xs text-text-secondary font-mono">WEATHER</div>
                        <div className="text-sm font-semibold">{weather ? `${weather.temperature}°` : '--'} {weather?.condition}</div>
                    </div>
                </div>
            </div>

            {/* Industrial Market Ticker */}
            <div className="bg-surface-card border border-border-strong rounded-xl p-3 flex items-center gap-6 overflow-x-auto shadow-sm no-scrollbar">
                <div className="flex items-center gap-2 text-text-secondary border-r border-border-subtle pr-4 shrink-0">
                    <TrendingUp className="w-4 h-4" />
                    <span className="font-mono text-xs font-bold uppercase tracking-wider">Commodities</span>
                </div>
                <div className="flex items-center gap-8 min-w-max">
                    {loading ? (
                        <span className="text-xs font-mono text-text-muted">SYNCING MARKET DATA...</span>
                    ) : (
                        marketData.map((item) => (
                            <div key={item.id} className="flex items-center gap-2 font-mono text-sm">
                                <span className="font-semibold text-text-primary">{item.name}</span>
                                <span className="text-text-primary">{item.price}</span>
                                <span className={`text-xs ${item.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                                    {item.change}%
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Main Grid */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

                {/* Crop Status Card */}
                <div className="col-span-12 lg:col-span-6 xl:col-span-6 bg-surface-card border border-border-strong rounded-xl p-6 shadow-sm flex flex-col justify-between relative overflow-hidden">
                    {/* Status Light Bar */}
                    <div className={`absolute top-0 left-0 right-0 h-1 ${healthScore > 80 ? 'bg-primary' : (healthScore > 50 ? 'bg-yellow-500' : 'bg-red-500')}`} />

                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h3 className="text-sm font-mono font-bold text-text-secondary uppercase tracking-wider">Environment HUD [{selectedField.name}]</h3>
                            <h2 className="text-2xl font-bold mt-1 text-text-primary">
                                {sensorData ? (healthScore > 80 ? 'Optimal' : 'Attention Needed') : 'Initializing...'}
                            </h2>
                        </div>
                        <button className="p-2 hover:bg-secondary rounded-lg transition-colors border border-transparent hover:border-border-subtle">
                            <MoreHorizontal className="w-4 h-4 text-text-secondary" />
                        </button>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-8">
                        {/* Gauge */}
                        <div className="relative w-40 h-40 flex-shrink-0">
                            {/* Simplified Gauge for Rugged Look */}
                            <svg className="w-full h-full transform -rotate-90">
                                <circle cx="80" cy="80" r="70" strokeWidth="12" fill="none" className="stroke-secondary" />
                                <circle
                                    cx="80" cy="80" r="70"
                                    strokeWidth="12"
                                    fill="none"
                                    strokeDasharray="440"
                                    strokeDashoffset={sensorData ? 440 - (440 * Math.round(healthScore)) / 100 : 440}
                                    strokeLinecap="butt"
                                    className={`transition-all duration-1000 ${healthScore > 75 ? 'stroke-primary' : (healthScore > 50 ? 'stroke-yellow-500' : 'stroke-red-500')}`}
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-4xl font-bold text-text-primary">
                                    {sensorData ? Math.round(healthScore) : '--'}
                                </span>
                                <span className="text-xs font-mono font-medium text-text-muted uppercase">Health Index</span>
                            </div>
                        </div>

                        {/* Metrics Grid */}
                        <div className="grid grid-cols-2 gap-4 w-full">
                            <HealthMetric
                                label="Moisture"
                                value={sensorData ? `${sensorData.moisture.toFixed(1)}%` : '--'}
                                color={sensorData?.moisture < 50 ? "text-yellow-600" : "text-blue-600"}
                            />
                            <HealthMetric
                                label="Chlorophyll"
                                value="Nominal"
                                color="text-primary"
                            />
                            <HealthMetric
                                label="Nitrogen"
                                value={sensorData ? `${sensorData.nitrogen.toFixed(1)}` : '--'}
                                color={sensorData?.nitrogen < 30 ? "text-red-500" : "text-text-primary"}
                            />
                            <HealthMetric
                                label="Harvest Est."
                                value="14 Days"
                            />
                        </div>
                    </div>
                </div>

                {/* Stats Column */}
                <div className="col-span-12 md:col-span-6 lg:col-span-3 space-y-4">
                    <StatCard
                        label="Air Temperature"
                        value={loading ? "..." : `${weather?.temperature}${weather?.unit}`}
                        subValue={loading ? "Locating..." : weather?.condition}
                        icon={Thermometer}
                        trend={!loading && weather?.temperature > 25 ? "+1.2%" : null}
                        trendUp={true}
                    />
                    <StatCard
                        label="Humidity"
                        value={loading ? "..." : `${weather?.humidity}%`}
                        subValue={loading ? "Locating..." : "Rel. Humidity"}
                        icon={Droplets}
                        trend="-0.5%"
                        trendUp={false}
                    />
                    <StatCard
                        label="Wind Speed"
                        value={loading ? "..." : `${weather?.windSpeed} km/h`}
                        subValue={loading ? "Locating..." : `${weather?.windDirection}°`}
                        icon={Wind}
                    />
                </div>

                {/* Field Assistant */}
                <div className="col-span-12 md:col-span-6 lg:col-span-3 bg-surface-card border border-border-strong rounded-xl p-5 flex flex-col shadow-sm">
                    <div className="flex items-center justify-between mb-4 border-b border-border-subtle pb-3">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-primary" />
                            <h3 className="font-bold text-sm uppercase tracking-wide text-text-primary">Field Assistant</h3>
                        </div>
                        <span className="text-[10px] uppercase font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">Active</span>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-3">
                        {sensorData && alerts.length > 0 ? (
                            alerts.map((alert, index) => (
                                <div key={index} className={`p-3 rounded-lg border text-sm ${alert.type === 'CRITICAL' ? 'bg-red-500/10 border-red-500/50 text-red-600' : 'bg-muted border-border-subtle text-text-primary'}`}>
                                    <div className="flex items-start gap-2">
                                        {alert.type === 'CRITICAL' && <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 shrink-0" />}
                                        <p className="leading-snug">
                                            <span className="font-semibold block text-xs uppercase mb-0.5 opacity-80">{alert.title}</span>
                                            {alert.message}
                                        </p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-3 rounded-lg bg-secondary border border-border-subtle">
                                <div className="flex items-start gap-2">
                                    <span className="w-2 h-2 rounded-full bg-green-500 mt-1.5 shrink-0" />
                                    <p className="text-sm text-text-secondary leading-snug">
                                        <span className="font-semibold block text-xs uppercase mb-0.5 text-text-primary">Status Nominal</span>
                                        No critical anomalies detected.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Biodiversity / Sustainability Insight */}
                        <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-bold uppercase text-primary">Bio-Index</span>
                                <Leaf className="w-3 h-3 text-primary" />
                            </div>
                            <div className="flex justify-between items-end">
                                <span className="text-2xl font-bold text-text-primary">88.4</span>
                                <span className="text-xs text-text-muted">+2.4% vs last year</span>
                            </div>
                        </div>

                        {/* Static Tip */}
                        <div className="p-3 rounded-lg bg-secondary border border-border-subtle opacity-70">
                            <p className="text-xs text-text-muted leading-snug font-mono">
                                &gt; TIP: Check {selectedField.name} irrigation lines.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function StatCard({ label, value, subValue, icon: Icon, trend, trendUp }) {
    return (
        <div className="bg-surface-card border border-border-strong rounded-xl p-4 flex items-start justify-between shadow-sm hover:border-primary/50 transition-colors">
            <div>
                <div className="flex items-center gap-2 mb-2 text-text-secondary">
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
                </div>
                <div className="text-2xl font-bold tracking-tight text-text-primary">{value}</div>
                {subValue && <div className="text-xs text-text-muted mt-1 font-mono">{subValue}</div>}
            </div>

            {trend && (
                <div className={`flex items-center text-xs font-bold px-2 py-1 rounded ${trendUp ? 'text-green-600 bg-green-500/10' : 'text-amber-600 bg-amber-500/10'}`}>
                    <ArrowUpRight className={`w-3 h-3 mr-1 ${!trendUp && 'rotate-90'}`} />
                    {trend}
                </div>
            )}
        </div>
    )
}

function HealthMetric({ label, value, color }) {
    return (
        <div>
            <div className="text-xs text-text-secondary mb-1 uppercase font-semibold">{label}</div>
            <div className={`text-lg font-bold font-mono ${color || 'text-text-primary'}`}>{value}</div>
        </div>
    )
}
